import { useLayoutEffect, useRef } from 'react';
import './ContinuousHero.css';

// Measure the document viewport, not the content frame (or 100vw with a scrollbar).
export function useContinuousHero(ref, enabled) {
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      // Rects use rendered pixels; CSS widths use the element's layout coordinates.
      // Account for inherited zoom without letting integer offsetWidth rounding move normal edges.
      const measuredScale = element.offsetWidth > 0 ? rect.width / element.offsetWidth : 1;
      const scale =
        Number.isFinite(measuredScale) && measuredScale > 0 && Math.abs(measuredScale - 1) > 0.01
          ? measuredScale
          : 1;
      element.style.setProperty(
        '--pp-hero-document-top',
        `${(rect.top + window.scrollY) / scale}px`,
      );
      element.style.setProperty('--pp-hero-left', `${-rect.left / scale}px`);
      element.style.setProperty(
        '--pp-hero-viewport',
        `${document.documentElement.clientWidth / scale}px`,
      );
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(element);
    observer?.observe(document.documentElement);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      ['--pp-hero-document-top', '--pp-hero-left', '--pp-hero-viewport'].forEach((key) =>
        element.style.removeProperty(key),
      );
    };
  }, [ref, enabled]);
}

// Only the media-route CSS consumes this value. Other headers stay opaque.
export function useHeaderScrollSurface() {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const paint = () => {
      const progress = Math.max(0, Math.min(1, window.scrollY / 96));
      ref.current?.style.setProperty(
        '--pp-header-surface',
        `${progress * progress * (3 - 2 * progress) * 100}%`,
      );
    };
    paint();
    window.addEventListener('scroll', paint, { passive: true });
    return () => window.removeEventListener('scroll', paint);
  }, []);
  return ref;
}
